"use client";

import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@lib/redux/hooks";
import {
  selectShortlistItems,
  selectShortlistLoading,
  selectShortlistError,
  selectShortlistInitialized,
  addToShortlist,
  removeFromShortlist,
  optimisticAdd,
  optimisticRemove,
  ShortlistCar,
} from "@lib/redux/slices/shortlistSlice";

export const useShortlist = () => {
  const dispatch = useAppDispatch();
  const items = useAppSelector(selectShortlistItems);
  const isLoading = useAppSelector(selectShortlistLoading);
  const error = useAppSelector(selectShortlistError);
  const initialized = useAppSelector(selectShortlistInitialized);

  const isCarShortlisted = useCallback(
    (carId: string) => items.some((item) => item.id === carId),
    [items]
  );

  const toggleShortlistItem = useCallback(
    async (car: ShortlistCar): Promise<{ success: boolean }> => {
      const isCurrentlyShortlisted = items.some((item) => item.id === car.id);

      if (isCurrentlyShortlisted) {
        dispatch(optimisticRemove(car.id));
        try {
          await dispatch(removeFromShortlist(car)).unwrap();
          return { success: true };
        } catch {
          return { success: false };
        }
      } else {
        dispatch(optimisticAdd(car));
        try {
          await dispatch(addToShortlist(car)).unwrap();
          return { success: true };
        } catch {
          return { success: false };
        }
      }
    },
    [dispatch, items]
  );

  return {
    items,
    isLoading,
    error,
    initialized,
    isCarShortlisted,
    toggleShortlistItem,
  };
};
